// src/pages/infoCentre/SessionFormation.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Layout from "../../components/Layout";
import SeancesEditor from "../../components/lab/SeancesEditor";
import ConflitSalle from "../../components/lab/ConflitSalle";
import authService from "../../services/auth/authService";
import {
  getSessions,
  getSessionsTerminees,
  ajouterSession,
  modifierSession,
  supprimerSessionsAvecConfirmation,
  reporterSession,
  reprogrammerSession,
  getNouvellesSessions,
  markSessionsAsRead,
} from "../../services/infoCentre/session_formation_service";
import { getFormateurs } from "../../services/infoCentre/Formateurservice";
import api from "../../services/api";
import "../../styles/infoCentre/sessionformation.css";

/* ── Constantes ── */
const MODE_LABELS = { presentiel: "Présentiel", distanciel: "Distanciel", hybride: "Hybride" };
const NIVEAU_LABELS = { debutant: "Débutant", intermediaire: "Intermédiaire", avance: "Avancé" };

const EMPTY_FORM = {
  intitule_session: "",
  formation: "",
  formateurs: [],
  date_debut: "",
  date_fin: "",
  mode: "presentiel",
  niveau: "",
  prix_ht: "",
  prix_ttc: "",
  tranche: "",
  seances: [],
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

const getFormations = async () => {
  try {
    const response = await api.get('formations/');
    const d = response.data;
    let formationsList = [];
    
    if (Array.isArray(d)) formationsList = d;
    else if (d?.results && Array.isArray(d.results)) formationsList = d.results;
    else if (d?.data && Array.isArray(d.data)) formationsList = d.data;
    else formationsList = [];
    
    return formationsList.filter(formation => formation.status === 'active');
  } catch (error) {
    console.error("Erreur chargement formations:", error.response?.status, error.message);
    return [];
  }
};

function NiveauBadge({ niveau }) {
  const map = { debutant: "bdg-deb", intermediaire: "bdg-int", avance: "bdg-adv" };
  
  if (!niveau) {
    return <span className="badge bdg-none">-</span>;
  }
  
  return <span className={`badge ${map[niveau] || ""}`}>{NIVEAU_LABELS[niveau] || niveau}</span>;
}

function ModeBadge({ mode }) {
  const map = { presentiel: "mode-presentiel", distanciel: "mode-distanciel", hybride: "mode-hybride" };
  const icons = { presentiel: "fa-chalkboard-teacher", distanciel: "fa-wifi", hybride: "fa-code-branch" };
  return (
    <span className={`mode-badge ${map[mode] || ""}`}>
      <i className={`fa-solid ${icons[mode] || "fa-circle"}`}></i>
      {MODE_LABELS[mode] || mode}
    </span>
  );
}

function StatutBadge({ statut }) {
  const icons = { 
    planifiee: "fa-calendar-day", 
    en_cours: "fa-play-circle", 
    terminee: "fa-check-circle",
    reportee: "fa-clock" 
  };
  const labels = { 
    planifiee: "Planifiée", 
    en_cours: "En cours", 
    terminee: "Terminée",
    reportee: "Reportée" 
  };
  
  let statusClass = "status-badge";
  if (statut === 'planifiee') statusClass += " status-planifiee";
  else if (statut === 'en_cours') statusClass += " status-en-cours";
  else if (statut === 'terminee') statusClass += " status-terminee";
  else if (statut === 'reportee') statusClass += " status-reportee";
  
  return (
    <span className={statusClass}>
      <i className={`fa-solid ${icons[statut] || "fa-circle"}`}></i>
      {labels[statut] || statut}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════
   SECTION ÉTUDIANTS dans le modal détail session
══════════════════════════════════════════════════════════ */
function SessionEtudiantsSection({ sessionId, onViewDetail }) {
  const [etudiants, setEtudiants] = useState([]);
  const [loadingEtudiants, setLoadingEtudiants] = useState(true);
  const [searchEtu, setSearchEtu] = useState("");

  useEffect(() => {
    if (!sessionId) return;
    setLoadingEtudiants(true);
    api.get(`sessions/${sessionId}/etudiants/`)
      .then((res) => setEtudiants(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error("Erreur chargement étudiants session:", err);
        setEtudiants([]);
      })
      .finally(() => setLoadingEtudiants(false));
  }, [sessionId]);

  const filtered = etudiants.filter((e) => {
    const q = searchEtu.toLowerCase();
    return (
      e.nom?.toLowerCase().includes(q) ||
      e.prenom?.toLowerCase().includes(q) ||
      e.email?.toLowerCase().includes(q) ||
      e.telephone?.toLowerCase().includes(q)
    );
  });

  const getInitiales = (nom, prenom) => {
    const n = (prenom?.[0] || "").toUpperCase();
    const p = (nom?.[0] || "").toUpperCase();
    return n + p || "?";
  };

  const avatarColors = [
    { bg: "#E8F0FE", color: "#336699" },
    { bg: "#E6F7F7", color: "#00A8A8" },
    { bg: "#FFF3E0", color: "#E67E22" },
    { bg: "#F3E5F5", color: "#8E44AD" },
    { bg: "#E8F5E9", color: "#27AE60" },
    { bg: "#FCE4EC", color: "#C0392B" },
  ];
  const getAvatarColor = (id) => avatarColors[id % avatarColors.length];

  return (
    <div className="detail-sec session-etudiants-section">
      <div className="detail-sec-title session-etudiants-title">
        <div className="session-etudiants-title-left">
          <i className="fa-solid fa-user-graduate"></i>
          Étudiants inscrits
          {!loadingEtudiants && (
            <span className="etudiants-count-badge">{etudiants.length}</span>
          )}
        </div>
      </div>

      {!loadingEtudiants && etudiants.length > 0 && (
        <div className="etudiants-search-bar">
          <i className="fa-solid fa-magnifying-glass"></i>
          <input
            type="text"
            placeholder="Rechercher par nom, prénom, email, téléphone..."
            value={searchEtu}
            onChange={(e) => setSearchEtu(e.target.value)}
          />
          {searchEtu && (
            <button
              className="etudiants-search-clear"
              onClick={() => setSearchEtu("")}
              type="button"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          )}
        </div>
      )}

      {loadingEtudiants ? (
        <div className="etudiants-loading">
          <i className="fa-solid fa-spinner fa-spin"></i>
          <span>Chargement des étudiants...</span>
        </div>
      ) : etudiants.length === 0 ? (
        <div className="etudiants-empty">
          <div className="etudiants-empty-icon">
            <i className="fa-solid fa-user-slash"></i>
          </div>
          <p>Aucun étudiant inscrit à cette session</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="etudiants-empty">
          <div className="etudiants-empty-icon">
            <i className="fa-solid fa-search"></i>
          </div>
          <p>Aucun résultat pour « {searchEtu} »</p>
        </div>
      ) : (
        <div className="etudiants-scroll-list">
          {filtered.map((e) => {
            const colors = getAvatarColor(e.id);
            return (
              <div key={e.id} className="etudiant-list-item">
                <div
                  className="etudiant-avatar"
                  style={{ background: colors.bg, color: colors.color }}
                >
                  {getInitiales(e.nom, e.prenom)}
                </div>

                <div className="etudiant-info">
                  <span className="etudiant-nom">
                    {e.prenom} {e.nom}
                  </span>
                  <div className="etudiant-contacts">
                    {e.email && (
                      <span className="etudiant-contact-item">
                        <i className="fa-solid fa-envelope"></i>
                        {e.email}
                      </span>
                    )}
                    {e.telephone && (
                      <span className="etudiant-contact-item">
                        <i className="fa-solid fa-phone"></i>
                        {e.telephone}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  className="etudiant-detail-btn"
                  title="Voir le détail de l'étudiant"
                  onClick={() => onViewDetail(e.id)}
                  type="button"
                >
                  <i className="fa-solid fa-eye"></i>
                  <span>Détails</span>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MODAL DÉTAIL ÉTUDIANT (DESIGN MODERNE - SANS NOTES)
══════════════════════════════════════════════════════════ */
function EtudiantDetailModal({ etudiantId, visible, onClose }) {
  const [etudiant, setEtudiant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!visible || !etudiantId) return;
    
    const fetchEtudiantDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        // La fiche vit sous /apprenants/ (la route /etudiants/ n'existe pas).
        const response = await api.get(`apprenants/${etudiantId}/`);
        setEtudiant(response.data);
      } catch (err) {
        console.error("Erreur chargement détail étudiant:", err);
        setError("Impossible de charger les détails de l'étudiant");
      } finally {
        setLoading(false);
      }
    };
    
    fetchEtudiantDetail();
  }, [etudiantId, visible]);

  if (!visible) return null;

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  const getStatutClass = (statut) => {
    switch(statut) {
      case 'actif': return 'status-active';
      case 'inactif': return 'status-inactive';
      case 'suspendu': return 'status-suspended';
      default: return 'status-default';
    }
  };

  const getStatutLabel = (statut) => {
    switch(statut) {
      case 'actif': return 'Actif';
      case 'inactif': return 'Inactif';
      case 'suspendu': return 'Suspendu';
      default: return statut || '—';
    }
  };

  const getGenreLabel = (genre) => {
    const genres = { H: "Homme", F: "Femme", autre: "Autre" };
    return genres[genre] || genre || "—";
  };

  const formationColors = [
    "#E8F0FE", "#E6F7F7", "#FFF3E0", "#F3E5F5", "#E8F5E9", "#FCE4EC", "#FFE0E0", "#E0F7FA"
  ];

  return (
    <div className="modal-overlay show" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal modal-etudiant-modern">
        <div className="modal-header modern-header">
          <button className="modal-close modern-close" onClick={onClose}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {/* EN-TÊTE PROFIL - FIXE (hors zone scrollable) */}
        {!loading && etudiant && (
          <div className="profile-header">
            <div className="profile-avatar">
              <span className="avatar-initials">
                {((etudiant.prenom?.[0] || '') + (etudiant.nom?.[0] || '')).toUpperCase() || '?'}
              </span>
            </div>
            <div className="profile-info">
              <h2 className="profile-name">
                {etudiant.prenom} {etudiant.nom}
              </h2>
              <div className="profile-badges">
                <span className={`profile-status ${getStatutClass(etudiant.statut)}`}>
                  <span className="status-dot"></span>
                  {getStatutLabel(etudiant.statut)}
                </span>
                {etudiant.mode_paiement && (
                  <span className="profile-payment">
                    <i className="fa-solid fa-credit-card"></i>
                    {etudiant.mode_paiement}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ZONE SCROLLABLE */}
        <div className="modern-body">
          {loading ? (
            <div className="modern-loading">
              <div className="modern-spinner"></div>
              <p>Chargement des informations...</p>
            </div>
          ) : error ? (
            <div className="modern-error">
              <i className="fa-solid fa-circle-exclamation"></i>
              <p>{error}</p>
            </div>
          ) : etudiant ? (
            <>
              {/* GRILLE INFORMATIONS PERSONNELLES */}
              <div className="info-section">
                <div className="section-header">
                  <i className="fa-solid fa-user-circle"></i>
                  <h3>Informations personnelles</h3>
                </div>
                <div className="info-grid-modern">
                  <div className="info-card">
                    <div className="info-icon"><i className="fa-solid fa-envelope"></i></div>
                    <div className="info-content">
                      <span className="info-label">Email</span>
                      <span className="info-value">{etudiant.email || "—"}</span>
                    </div>
                  </div>
                  <div className="info-card">
                    <div className="info-icon"><i className="fa-solid fa-phone"></i></div>
                    <div className="info-content">
                      <span className="info-label">Téléphone</span>
                      <span className="info-value">{etudiant.telephone || "—"}</span>
                    </div>
                  </div>
                  <div className="info-card">
                    <div className="info-icon"><i className="fa-solid fa-location-dot"></i></div>
                    <div className="info-content">
                      <span className="info-label">Ville / Pays</span>
                      <span className="info-value">
                        {etudiant.ville || "—"} {etudiant.pays ? `(${etudiant.pays})` : ""}
                      </span>
                    </div>
                  </div>
                  <div className="info-card">
                    <div className="info-icon"><i className="fa-solid fa-cake-candles"></i></div>
                    <div className="info-content">
                      <span className="info-label">Date de naissance</span>
                      <span className="info-value">{formatDate(etudiant.date_naissance)}</span>
                    </div>
                  </div>
                  <div className="info-card">
                    <div className="info-icon"><i className="fa-solid fa-venus-mars"></i></div>
                    <div className="info-content">
                      <span className="info-label">Genre</span>
                      <span className="info-value">{getGenreLabel(etudiant.genre)}</span>
                    </div>
                  </div>
                  <div className="info-card">
                    <div className="info-icon"><i className="fa-solid fa-graduation-cap"></i></div>
                    <div className="info-content">
                      <span className="info-label">Niveau d'études</span>
                      <span className="info-value">{etudiant.niveau_etudes || "—"}</span>
                    </div>
                  </div>
                  <div className="info-card">
                    <div className="info-icon"><i className="fa-solid fa-graduation-cap"></i></div>
                    <div className="info-content">
                      <span className="info-label">Diplôme obtenu</span>
                      <span className="info-value">{etudiant.diplome_obtenu || "—"}</span>
                    </div>
                  </div>
                  <div className="info-card">
                    <div className="info-icon"><i className="fa-solid fa-calendar-check"></i></div>
                    <div className="info-content">
                      <span className="info-label">Date d'inscription</span>
                      <span className="info-value">{formatDate(etudiant.date_inscription)}</span>
                    </div>
                  </div>
                  <div className="info-card">
                    <div className="info-icon"><i className="fa-solid fa-user-tie"></i></div>
                    <div className="info-content">
                      <span className="info-label">Responsable</span>
                      <span className="info-value">{etudiant.responsable_nom || "—"}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* FORMATIONS SUIVIES */}
              {etudiant.formations_suivies_detail && etudiant.formations_suivies_detail.length > 0 && (
                <div className="formations-section">
                  <div className="section-header">
                    <i className="fa-solid fa-book-open"></i>
                    <h3>Formations suivies</h3>
                    <span className="section-count">{etudiant.formations_suivies_detail.length}</span>
                  </div>
                  <div className="formations-list">
                    {etudiant.formations_suivies_detail.map((f, idx) => {
                      const colorIndex = idx % formationColors.length;
                      return (
                        <div key={idx} className="formation-card">
                          <div className="formation-icon" style={{ backgroundColor: formationColors[colorIndex] }}>
                            <i className="fa-solid fa-graduation-cap"></i>
                          </div>
                          <div className="formation-details">
                            <div className="formation-title">{f.intitule}</div>
                            <div className="formation-meta">
                              <span className="meta-item">
                                <i className="fa-solid fa-clock"></i>
                                {f.duree} heures
                              </span>
                              {f.session_range && (
                                <span className="meta-item">
                                  <i className="fa-solid fa-calendar"></i>
                                  {f.session_range}
                                </span>
                              )}
                            </div>
                            {f.attestation && (
                              <div className="formation-attestation">
                                <i className="fa-solid fa-certificate"></i>
                                Attestation disponible
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* DOCUMENTS */}
              {etudiant.documents && etudiant.documents.length > 0 && (
                <div className="documents-section">
                  <div className="section-header">
                    <i className="fa-solid fa-file"></i>
                    <h3>Documents</h3>
                    <span className="section-count">{etudiant.documents.length}</span>
                  </div>
                  <div className="documents-list">
                    {etudiant.documents.map((doc) => (
                      <div key={doc.id} className="document-item">
                        <div className="document-icon">
                          <i className="fa-solid fa-file-pdf"></i>
                        </div>
                        <div className="document-info">
                          <div className="document-name">{doc.type_display || doc.type_document}</div>
                          <div className="document-date">
                            {doc.date_upload && `Ajouté le ${formatDate(doc.date_upload)}`}
                            {doc.commentaire && ` • ${doc.commentaire}`}
                          </div>
                        </div>
                        {doc.fichier && (
                          <a href={doc.fichier} target="_blank" rel="noopener noreferrer" className="document-link">
                            <i className="fa-solid fa-download"></i>
                            Télécharger
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* RELANCES */}
              {etudiant.relances && etudiant.relances.length > 0 && (
                <div className="relances-section">
                  <div className="section-header">
                    <i className="fa-solid fa-bell"></i>
                    <h3>Historique des relances</h3>
                    <span className="section-count">{etudiant.relances.length}</span>
                  </div>
                  <div className="relances-timeline">
                    {etudiant.relances.slice(0, 5).map((relance) => (
                      <div key={relance.id} className="timeline-item">
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                          <div className="timeline-header">
                            <span className="timeline-date">
                              {formatDate(relance.date_relance)}
                            </span>
                            {relance.formation_nom && (
                              <span className="timeline-formation">
                                <i className="fa-solid fa-book"></i>
                                {relance.formation_nom}
                              </span>
                            )}
                            {relance.statut_calcule && (
                              <span className={`timeline-status ${relance.statut_calcule === "converti" ? "converted" : "pending"}`}>
                                {relance.statut_calcule === "converti" ? "Converti" : "En attente"}
                              </span>
                            )}
                          </div>
                          {relance.commentaire && (
                            <div className="timeline-comment">
                              <i className="fa-solid fa-message"></i>
                              {relance.commentaire}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {etudiant.relances.length > 5 && (
                      <div className="timeline-more">
                        +{etudiant.relances.length - 5} autres relances
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        <div className="modal-footer modern-footer">
          <button className="btn-modern btn-modern-secondary" onClick={onClose}>
            <i className="fa-solid fa-xmark"></i>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

function ReprogrammerModal({ session, onClose, onReprogrammer, loading }) {
  const [dates, setDates] = useState({
    date_debut: "",
    date_fin: "",
  });
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (!dates.date_debut || !dates.date_fin) {
      setError("Les deux dates sont obligatoires");
      return;
    }
    if (dates.date_debut >= dates.date_fin) {
      setError("La date de fin doit être postérieure à la date de début");
      return;
    }
    onReprogrammer(dates);
  };

  return (
    <div className="modal-overlay show" onClick={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}>
      <div className="modal modal-reprogrammer">
        <div className="modal-header">
          <h2><i className="fa-solid fa-calendar-plus"></i> Reprogrammer la session</h2>
          <button className="modal-close" onClick={onClose} disabled={loading}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div className="modal-body">
          <div className="reprogrammer-session-info">
            <i className="fa-solid fa-book-open"></i>
            <span>{session.intitule_session}</span>
          </div>
          
          {error && (
            <div className="error-server" style={{ marginBottom: "20px" }}>
              <i className="fa-solid fa-circle-exclamation"></i> {error}
            </div>
          )}
          
          <div className="form-grid">
            <div className="form-group">
              <label>Nouvelle date de début <span className="req">*</span></label>
              <input
                type="date"
                value={dates.date_debut}
                onChange={(e) => {
                  setDates({ ...dates, date_debut: e.target.value });
                  setError("");
                }}
              />
            </div>
            <div className="form-group">
              <label>Nouvelle date de fin <span className="req">*</span></label>
              <input
                type="date"
                min={dates.date_debut || undefined}
                value={dates.date_fin}
                onChange={(e) => {
                  setDates({ ...dates, date_fin: e.target.value });
                  setError("");
                }}
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-cancel" onClick={onClose} disabled={loading}>Annuler</button>
          <button className="btn btn-add" onClick={handleSubmit} disabled={loading}>
            {loading ? <><i className="fa-solid fa-spinner fa-spin"></i> En cours...</> : <><i className="fa-solid fa-calendar-check"></i> Reprogrammer</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilterDropdown({ icon, placeholder, items, selectedValue, onSelect, getLabel }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const filtered = items.filter((it) =>
    getLabel(it).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="sf-filter-dropdown" ref={ref}>
      <button type="button" className="filter-dropdown-btn" onClick={() => setOpen(!open)}>
        <i className={`fa-solid ${icon}`}></i>
        <span className="filter-dropdown-text">{selectedValue ? getLabel(selectedValue) : placeholder}</span>
        <i className={`fa-solid fa-chevron-${open ? "up" : "down"} filter-dropdown-chevron`}></i>
      </button>
      {open && (
        <div className="filter-dropdown-panel">
          <div className="filter-dropdown-search">
            <i className="fa-solid fa-magnifying-glass"></i>
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="filter-dropdown-list">
            <div
              className={`filter-dropdown-item ${!selectedValue ? "active" : ""}`}
              onClick={() => { onSelect(""); setOpen(false); setSearch(""); }}
            >
              <i className="fa-solid fa-arrow-rotate-left"></i>
              <span>{placeholder}</span>
            </div>
            {filtered.length === 0 ? (
              <div className="filter-dropdown-empty">Aucun résultat</div>
            ) : (
              filtered.map((it, idx) => (
                <div
                  key={idx}
                  className={`filter-dropdown-item ${selectedValue === it ? "active" : ""}`}
                  onClick={() => { onSelect(it); setOpen(false); setSearch(""); }}
                >
                  <i className="fa-solid fa-circle-dot"></i>
                  <span>{getLabel(it)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FormationFormSelect({ formations, value, onChange, hasError, disabled }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const selected = formations.find((f) => f.id === value);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const filtered = formations.filter((f) =>
    (f.intitule || f.intitule_formation || f.nom || "")?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="sf-form-select" ref={ref}>
      <button
        type="button"
        className={`sf-select-btn ${hasError ? "error-border" : ""}`}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
      >
        <span className={`sf-select-text ${!value ? "sf-select-placeholder" : ""}`}>
          {selected ? (selected.intitule || selected.intitule_formation || selected.nom) : "Sélectionner une formation"}
        </span>
        {!disabled && <i className={`fa-solid fa-chevron-${open ? "up" : "down"} sf-select-chevron`}></i>}
      </button>
      {!disabled && open && (
        <div className="sf-dropdown-panel">
          <div className="sf-dropdown-search">
            <i className="fa-solid fa-magnifying-glass"></i>
            <input
              type="text"
              placeholder="Rechercher une formation..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            {search && (
              <button type="button" className="sf-search-clear" onClick={() => setSearch("")}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            )}
          </div>
          <div className="sf-dropdown-list">
            {filtered.length === 0 ? (
              <div className="sf-dropdown-empty">Aucune formation trouvée</div>
            ) : (
              filtered.map((f) => (
                <div
                  key={f.id}
                  className={`sf-dropdown-item ${value === f.id ? "active" : ""}`}
                  onClick={() => { onChange(f.id); setOpen(false); setSearch(""); }}
                >
                  <i className="fa-solid fa-book-open"></i>
                  <span>{f.intitule || f.intitule_formation || f.nom}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FormateursMultiSelect({ formateurs, value, onChange, disabled }) {
  const [search, setSearch] = useState("");
  
  const filtered = (formateurs || []).filter((f) =>
    `${f.prenom || ''} ${f.nom || ''} ${f.specialites || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id) => {
    if (disabled) return;
    const newValue = value.includes(id) ? value.filter((x) => x !== id) : [...value, id];
    onChange(newValue);
  };

  if (!formateurs || formateurs.length === 0) {
    return (
      <div className="fmt-multiselect-wrap">
        <div className="fmt-multiselect-empty" style={{ padding: "20px", textAlign: "center", color: "#94a3b8" }}>
          <i className="fa-solid fa-spinner fa-spin"></i> Chargement des formateurs...
        </div>
      </div>
    );
  }

  return (
    <div className="fmt-multiselect-wrap">
      <div className="fmt-multiselect-search">
        <i className="fa-solid fa-magnifying-glass"></i>
        <input
          type="text"
          placeholder="Rechercher un formateur (nom, prénom, spécialité)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled}
        />
        {search && !disabled && (
          <button type="button" className="fmt-search-clear" onClick={() => setSearch("")}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        )}
      </div>
      <div className="fmt-multiselect-list">
        {filtered.length === 0 ? (
          <div className="fmt-multiselect-empty">Aucun formateur trouvé</div>
        ) : (
          filtered.map((f) => {
            const checked = value.includes(f.id);
            const nomComplet = `${f.prenom || ''} ${f.nom || ''}`.trim();
            const specialites = f.specialites || '';
            const specialiteDisplay = specialites.length > 35 ? specialites.substring(0, 35) + '...' : specialites;
            
            return (
              <div
                key={f.id}
                className={`fmt-multiselect-item ${checked ? "checked" : ""} ${disabled ? "item-disabled" : ""}`}
                onClick={() => toggle(f.id)}
              >
                <span className={`fmt-checkbox ${checked ? "checked" : ""}`}>
                  {checked && <i className="fa-solid fa-check" style={{ fontSize: "10px", color: "#fff" }}></i>}
                </span>
                <span className="fmt-name">
                  {nomComplet}
                  {specialites && (
                    <span style={{ fontSize: "11px", color: "#64748b", marginLeft: "5px" }}>
                      ({specialiteDisplay})
                    </span>
                  )}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
function SessionFormation() {
  const navigate = useNavigate();
  const location = useLocation();
  const isSuperAdmin = authService.isSuperAdmin();
  // Planifier (ajouter, modifier, reporter) : aussi l'assistante (agent
  // marketing). Supprimer : la gestion seule, comme côté serveur.
  const peutGerer  = authService.peutPlanifier();
  const estGestion = authService.isGestion();

  // ── Notification nouvelles sessions (agent marketing uniquement) ──
  const [newSessions, setNewSessions] = useState([]);
  const [markingSessionRead, setMarkingSessionRead] = useState(false);

  const [activeTab, setActiveTab] = useState("actives");
  const [sessions, setSessions] = useState([]);
  const [sessionsTerminees, setSessionsTerminees] = useState([]);
  const [formations, setFormations] = useState([]);
  const [formateurs, setFormateurs] = useState([]);
  const [loading, setLoading] = useState(true);

  const modalAjoutRef = useRef(null);
  const modalModifRef = useRef(null);

  // Filtres tab actives
  const [search, setSearch] = useState("");
  const [filterFormation, setFilterFormation] = useState("");
  const [filterNiveau, setFilterNiveau] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  
  const [filterFormationById, setFilterFormationById] = useState(null);
  const [filterFormationByName, setFilterFormationByName] = useState("");

  // Filtres tab terminées
  const [searchTerm, setSearchTerm] = useState("");
  const [filterFormationTerm, setFilterFormationTerm] = useState("");
  const [filterNiveauTerm, setFilterNiveauTerm] = useState("");
  const [currentPageTerm, setCurrentPageTerm] = useState(1);

  // Sélections bulk (uniquement pour Super Admin)
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Modales
  const [modalDetail, setModalDetail] = useState(null);
  const [modalDetailEtudiant, setModalDetailEtudiant] = useState(null); // 👈 AJOUTÉ
  const [modalAjout, setModalAjout] = useState(false);
  const [modalModif, setModalModif] = useState(null);
  const [modalSuppr, setModalSuppr] = useState(null);
  const [modalReprogrammer, setModalReprogrammer] = useState(null);
  const [modalReportConfirm, setModalReportConfirm] = useState(null);

  const [formAjout, setFormAjout] = useState(EMPTY_FORM);
  const [formModif, setFormModif] = useState(EMPTY_FORM);
  const [erreursAjout, setErreursAjout] = useState({});
  const [erreursModif, setErreursModif] = useState({});
  const [errServeurAjout, setErrServeurAjout] = useState("");
  const [errServeurModif, setErrServeurModif] = useState("");
  const [succesGlobal, setSuccesGlobal] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  // Salle déjà occupée (réponse « conflit_salle ») : modifier les séances, ou
  // maintenir (salle partagée par deux groupes). Caduc dès que les séances changent.
  const [conflitAjout, setConflitAjout] = useState(null);
  const [conflitModif, setConflitModif] = useState(null);
  useEffect(() => { setConflitAjout(null); }, [formAjout.seances, formAjout.mode, modalAjout]);
  useEffect(() => { setConflitModif(null); }, [formModif.seances, formModif.mode, modalModif]);
  const [reportLoading, setReportLoading] = useState(null);

  const itemsPerPage = 7;

  // ============================================
  // INITIALISATION - Charge TOUTES les données au chargement
  // ============================================
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          (async () => {
            const sessionsData = await getSessions();
            let normalized = sessionsData;
            if (!Array.isArray(sessionsData)) {
              if (sessionsData?.results && Array.isArray(sessionsData.results)) normalized = sessionsData.results;
              else if (sessionsData?.data && Array.isArray(sessionsData.data)) normalized = sessionsData.data;
              else normalized = [];
            }
            setSessions(normalized);
          })(),
          (async () => {
            const termineesData = await getSessionsTerminees();
            let normalized = termineesData;
            if (!Array.isArray(termineesData)) {
              if (termineesData?.results && Array.isArray(termineesData.results)) normalized = termineesData.results;
              else if (termineesData?.data && Array.isArray(termineesData.data)) normalized = termineesData.data;
              else normalized = [];
            }
            setSessionsTerminees(normalized);
          })(),
          (async () => {
            const formationsData = await getFormations();
            setFormations(Array.isArray(formationsData) ? formationsData : []);
          })(),
          (async () => {
            const formateursData = await getFormateurs();
            setFormateurs(Array.isArray(formateursData) ? formateursData : []);
          })(),
        ]);
        setCurrentPage(1);
        setCurrentPageTerm(1);
      } catch (err) {
        console.error("Erreur chargement initial:", err);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  // Charger les nouvelles sessions au montage (agent marketing uniquement)
  useEffect(() => {
    if (isSuperAdmin) return;
    const fetchNewSessions = async () => {
      try {
        const res = await getNouvellesSessions();
        if (res.data.success) {
          setNewSessions(res.data.sessions);
        }
      } catch (err) {
        console.error("Erreur chargement nouvelles sessions:", err);
      }
    };
    fetchNewSessions();
  }, []);

  // ============================================
  // RAFRAÎCHISSEMENT QUAND ON CHANGE D'ONGLET
  // ============================================
  useEffect(() => {
    if (activeTab === "terminees") {
      const refreshTerminees = async () => {
        const data = await getSessionsTerminees();
        let normalized = data;
        if (!Array.isArray(data)) {
          if (data?.results && Array.isArray(data.results)) normalized = data.results;
          else if (data?.data && Array.isArray(data.data)) normalized = data.data;
          else normalized = [];
        }
        setSessionsTerminees(normalized);
        setCurrentPageTerm(1);
      };
      refreshTerminees();
    }
    setSelectedIds([]);
  }, [activeTab]);

  // ============================================
  // RÉCUPÉRATION DU FILTRE PAR FORMATION DEPUIS L'URL
  // ============================================
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const formationId = params.get('formation_id');
    const formationName = params.get('formation_name');
    
    if (formationId) {
      setFilterFormationById(parseInt(formationId));
      setFilterFormationByName(formationName ? decodeURIComponent(formationName) : "");
      setActiveTab('actives');
      setCurrentPage(1);
    }
  }, [location.search]);

  const scrollToTopAjout = () => {
    setTimeout(() => {
      if (modalAjoutRef.current) {
        const modalBody = modalAjoutRef.current.querySelector('.modal-body');
        if (modalBody) modalBody.scrollTop = 0;
      }
    }, 50);
  };

  const scrollToTopModif = () => {
    setTimeout(() => {
      if (modalModifRef.current) {
        const modalBody = modalModifRef.current.querySelector('.modal-body');
        if (modalBody) modalBody.scrollTop = 0;
      }
    }, 50);
  };

  const fetchAll = async () => {
    try {
      const [sessionsData, formationsData, formateursData] = await Promise.all([
        getSessions(),
        getFormations(),
        getFormateurs(),
      ]);

      let normalizedSessions = sessionsData;
      if (!Array.isArray(sessionsData)) {
        if (sessionsData?.results && Array.isArray(sessionsData.results)) normalizedSessions = sessionsData.results;
        else if (sessionsData?.data && Array.isArray(sessionsData.data)) normalizedSessions = sessionsData.data;
        else normalizedSessions = [];
      }

      setSessions(normalizedSessions);
      setFormations(Array.isArray(formationsData) ? formationsData : []);
      setFormateurs(Array.isArray(formateursData) ? formateursData : []);

      setCurrentPage(1);
    } catch (err) {
      console.error("Erreur fetchAll:", err);
    }
  };

  const fetchTerminees = async () => {
    try {
      const data = await getSessionsTerminees();
      let normalizedData = data;
      if (!Array.isArray(data)) {
        if (data?.results && Array.isArray(data.results)) normalizedData = data.results;
        else if (data?.data && Array.isArray(data.data)) normalizedData = data.data;
        else normalizedData = [];
      }
      setSessionsTerminees(normalizedData);
      setCurrentPageTerm(1);
    } catch (err) {
      console.error("Erreur fetchTerminees:", err);
    }
  };

  const afficherSucces = (msg) => {
    setSuccesGlobal(msg);
    setTimeout(() => setSuccesGlobal(""), 4000);
  };

  const handleMarkSessionAsRead = async () => {
    setMarkingSessionRead(true);
    try {
      await markSessionsAsRead();
      setNewSessions([]);
    } catch (err) {
      console.error("Erreur lors du marquage des sessions comme lu:", err);
    } finally {
      setMarkingSessionRead(false);
    }
  };

  const handleReporter = (session) => {
    if (!peutGerer) return;
    setModalReportConfirm(session);
  };

  const handleReprogrammer = async (session, dates) => {
    if (!peutGerer) return;
    setSubmitLoading(true);
    try {
      await reprogrammerSession(session.id, dates);
      await fetchAll();
      await fetchTerminees();
      setModalReprogrammer(null);
      afficherSucces(`Session "${session.intitule_session}" a été reprogrammée`);
    } catch (err) {
      console.error("Erreur lors de la reprogrammation:", err);
      if (err.response?.data?.error) {
        alert(err.response.data.error);
      } else {
        alert("Erreur lors de la reprogrammation de la session");
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const valider = (form) => {
    const errs = {};
    if (!form.intitule_session?.trim()) errs.intitule_session = "L'intitulé est requis";
    if (!form.formation) errs.formation = "La formation est requise";
    if (!form.date_debut) errs.date_debut = "La date de début est requise";
    if (!form.date_fin) errs.date_fin = "La date de fin est requise";
    // Égales : session d'un seul jour (une séance), désormais possible.
    if (form.date_debut && form.date_fin && form.date_debut > form.date_fin)
      errs.date_fin = "La date de fin ne peut pas précéder la date de début";
    // Prix et tranches sont facultatifs ; contrôlés seulement s'ils sont saisis.
    if (form.prix_ht !== "" && parseFloat(form.prix_ht) < 0) errs.prix_ht = "Le prix HT ne peut pas être négatif";
    if (form.prix_ttc !== "" && parseFloat(form.prix_ttc) < 0) errs.prix_ttc = "Le prix TTC ne peut pas être négatif";
    if (form.prix_ht && form.prix_ttc && parseFloat(form.prix_ht) > parseFloat(form.prix_ttc))
      errs.prix_ttc = "Le prix TTC doit être ≥ au prix HT";
    if (form.tranche !== "" && parseInt(form.tranche) < 1) errs.tranche = "Le nombre de tranches doit être ≥ 1";
    const seances = form.seances || [];
    if (!seances.length)
      errs.seances = "Ajoutez au moins une séance (jours fixes ou dates personnalisées).";
    else if (seances.some((s) => !s.date || !s.heure_debut || !s.heure_fin || s.heure_fin <= s.heure_debut))
      errs.seances = "Vérifiez les séances : chacune doit avoir une date et une heure de fin après l'heure de début.";
    return errs;
  };

  // Appelé par le bouton (un événement) ou par « Maintenir » ({ salle_partagee: true }).
  const handleAjout = async (options) => {
    if (!peutGerer) return;
    const salle_partagee = options?.salle_partagee === true;
    const errs = valider(formAjout);
    if (Object.keys(errs).length > 0) { 
      setErreursAjout(errs);
      scrollToTopAjout();
      return; 
    }
    try {
      setSubmitLoading(true);
      setErreursAjout({});
      setErrServeurAjout("");
      await ajouterSession({
        ...formAjout,
        formation: parseInt(formAjout.formation),
        prix_ht: formAjout.prix_ht === "" ? null : parseFloat(formAjout.prix_ht),
        prix_ttc: formAjout.prix_ttc === "" ? null : parseFloat(formAjout.prix_ttc),
        tranche: formAjout.tranche === "" ? null : parseInt(formAjout.tranche),
        // Le serveur refuse null pour le niveau : « aucun » s'écrit "".
        niveau: formAjout.niveau || "",
        seances: formAjout.seances || [],
        salle_partagee,
      });
      await fetchAll();
      await fetchTerminees();
      setModalAjout(false);
      setFormAjout(EMPTY_FORM);
      afficherSucces("Session ajoutée avec succès !");
    } catch (err) {
      if (err.response?.data) {
        const errorData = err.response.data;
        if (errorData.conflit_salle) {
          setConflitAjout([].concat(errorData.conflit_salle));
        } else if (errorData.seances) {
          setErreursAjout({ seances: [].concat(errorData.seances).join("\n") });
        } else if (errorData.dates) {
          setErreursAjout({ dates: errorData.dates });
          scrollToTopAjout();
        } else {
          setErrServeurAjout(Object.values(errorData).flat().join("\n"));
          scrollToTopAjout();
        }
      } else {
        setErrServeurAjout("Erreur serveur");
        scrollToTopAjout();
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleModif = async (options) => {
    if (!peutGerer) return;
    const salle_partagee = options?.salle_partagee === true;
    if (!modalModif) return;
    const errs = valider(formModif);
    if (Object.keys(errs).length > 0) { 
      setErreursModif(errs);
      scrollToTopModif();
      return; 
    }
    try {
      setSubmitLoading(true);
      setErreursModif({});
      setErrServeurModif("");
      await modifierSession(modalModif.id, {
        ...formModif,
        formation: parseInt(formModif.formation),
        prix_ht: formModif.prix_ht === "" ? null : parseFloat(formModif.prix_ht),
        prix_ttc: formModif.prix_ttc === "" ? null : parseFloat(formModif.prix_ttc),
        tranche: formModif.tranche === "" ? null : parseInt(formModif.tranche),
        // Le serveur refuse null pour le niveau : « aucun » s'écrit "".
        niveau: formModif.niveau || "",
        seances: formModif.seances || [],
        salle_partagee,
      });
      await fetchAll();
      await fetchTerminees();
      setModalModif(null);
      afficherSucces("Session modifiée avec succès !");
    } catch (err) {
      if (err.response?.data) {
        const errorData = err.response.data;
        if (errorData.conflit_salle) {
          setConflitModif([].concat(errorData.conflit_salle));
        } else if (errorData.seances) {
          setErreursModif({ seances: [].concat(errorData.seances).join("\n") });
        } else if (errorData.dates) {
          setErreursModif({ dates: errorData.dates });
          scrollToTopModif();
        } else {
          setErrServeurModif(Object.values(errorData).flat().join("\n"));
          scrollToTopModif();
        }
      } else {
        setErrServeurModif("Erreur serveur");
        scrollToTopModif();
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSupprimer = async () => {
    if (!estGestion) return;
    if (!modalSuppr) return;
    try {
      setSubmitLoading(true);
      const { supprimees, echecs } = await supprimerSessionsAvecConfirmation([modalSuppr.id]);
      if (supprimees) {
        await fetchAll();
        await fetchTerminees();
        setModalSuppr(null);
        afficherSucces("Session supprimée avec succès !");
      } else if (echecs.length) {
        alert(echecs[0]);
      }
      // Sinon : la confirmation a été refusée, rien n'est supprimé.
    } finally {
      setSubmitLoading(false);
    }
  };

  const changerSeancesAjout = useCallback((seances) => {
    setFormAjout((f) => ({ ...f, seances }));
    setErreursAjout((e) => (e.seances ? { ...e, seances: "" } : e));
  }, []);
  const changerSeancesModif = useCallback((seances) => {
    setFormModif((f) => ({ ...f, seances }));
    setErreursModif((e) => (e.seances ? { ...e, seances: "" } : e));
  }, []);

  const openModif = (s) => {
    if (!peutGerer) return;
    setFormModif({
      intitule_session: s.intitule_session || "",
      formation: s.formation || "",
      formateurs: Array.isArray(s.formateurs) ? s.formateurs : [],
      date_debut: s.date_debut ? s.date_debut.slice(0, 10) : "",
      date_fin: s.date_fin ? s.date_fin.slice(0, 10) : "",
      mode: s.mode || "presentiel",
      niveau: s.niveau || "",
      prix_ht: s.prix_ht || "",
      prix_ttc: s.prix_ttc || "",
      tranche: s.tranche || "",
      seances: (s.seances || []).map((se) => ({
        date: se.date, heure_debut: se.heure_debut.slice(0, 5), heure_fin: se.heure_fin.slice(0, 5),
      })),
    });
    setModalModif(s);
  };

  const handleOverlay = (e, fn) => { if (e.target === e.currentTarget) fn(); };

  const formationNoms = [...new Set(sessions.map((s) => s.formation_nom).filter(Boolean))];
  const formationNomsTerminees = [...new Set(sessionsTerminees.map((s) => s.formation_nom).filter(Boolean))];
  const niveaux = ["debutant", "intermediaire", "avance"];

  const filteredActives = sessions.filter((s) => {
    if (filterFormationById) {
      if (s.formation !== filterFormationById) return false;
    }
    
    const q = search.toLowerCase();
    const matchQ = s.intitule_session?.toLowerCase().includes(q) || s.formation_nom?.toLowerCase().includes(q);
    const matchFormation = !filterFormation || s.formation_nom === filterFormation;
    const matchNiveau = !filterNiveau || s.niveau === filterNiveau;
    return matchQ && matchFormation && matchNiveau;
  }).sort((a, b) => new Date(b.date_creation) - new Date(a.date_creation));

  const totalPages = Math.max(1, Math.ceil(filteredActives.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * itemsPerPage;
  const paginated = filteredActives.slice(startIdx, startIdx + itemsPerPage);

  const filteredTerminees = sessionsTerminees.filter((s) => {
    const q = searchTerm.toLowerCase();
    const matchQ = s.intitule_session?.toLowerCase().includes(q) || s.formation_nom?.toLowerCase().includes(q);
    const matchFormation = !filterFormationTerm || s.formation_nom === filterFormationTerm;
    const matchNiveau = !filterNiveauTerm || s.niveau === filterNiveauTerm;
    return matchQ && matchFormation && matchNiveau;
  }).sort((a, b) => new Date(b.date_creation) - new Date(a.date_creation));

  const totalPagesTerm = Math.max(1, Math.ceil(filteredTerminees.length / itemsPerPage));
  const safePageTerm = Math.min(currentPageTerm, totalPagesTerm);
  const startIdxTerm = (safePageTerm - 1) * itemsPerPage;
  const paginatedTerm = filteredTerminees.slice(startIdxTerm, startIdxTerm + itemsPerPage);

  // Sélections bulk (uniquement pour Super Admin)
  const toggleSelect = (id) => {
    if (!estGestion) return;
    setSelectedIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  };
  const toggleSelectAll = () => {
    if (!estGestion) return;
    setSelectedIds(selectedIds.length === paginated.length && paginated.length > 0 ? [] : paginated.map((s) => s.id));
  };
  const allChecked = paginated.length > 0 && selectedIds.length === paginated.length;
  const someChecked = selectedIds.length > 0 && selectedIds.length < paginated.length;

  const resetFilterById = () => {
    setFilterFormationById(null);
    setFilterFormationByName("");
    navigate('/sessions-formation', { replace: true });
  };

  if (loading && !sessions.length) {
    return (
      <Layout>
        <div className="loading-container">
          <i className="fa-solid fa-spinner fa-spin"></i>
          <p>Chargement des sessions...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header">
        <h1 className="page-title">
          <i className="fa-solid fa-calendar-days"></i> Gestion des Sessions de Formation
        </h1>
        <p className="page-sub">Suivi et planification des sessions du centre</p>
      </div>

      {succesGlobal && (
        <div className="success-message">
          <i className="fa-solid fa-check-circle"></i> {succesGlobal}
        </div>
      )}

      {/* ── Bannière nouvelles sessions (Agent Marketing uniquement) ── */}
      {!isSuperAdmin && newSessions.length > 0 && (
        <div className="notification-banner">
          <div className="notification-banner-content">
            <i className="fa-solid fa-bell"></i>
            <div className="notification-text">
              <strong>
                {newSessions.length === 1
                  ? "Nouvelle session disponible :"
                  : `${newSessions.length} nouvelles sessions disponibles :`}
              </strong>
              <ul>
                {newSessions.map(s => (
                  <li key={s.id}>
                    {s.intitule_session}
                    {s.formation_nom && (
                      <span className="notification-date"> — {s.formation_nom}</span>
                    )}
                    <span className="notification-date">
                      {" "}(créée le {new Date(s.date_creation).toLocaleDateString('fr-FR')})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <button
              className="notification-mark-read"
              onClick={handleMarkSessionAsRead}
              disabled={markingSessionRead}
            >
              {markingSessionRead
                ? <i className="fa-solid fa-spinner fa-spin"></i>
                : <><i className="fa-solid fa-check"></i> Marquer comme lu</>}
            </button>
          </div>
        </div>
      )}

      <div className="sessions-tabs">
        <button
          className={`tab-btn ${activeTab === "actives" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("actives")}
        >
          <i className="fa-solid fa-play-circle"></i>
          Sessions actives
          <span className="tab-count">{sessions.length}</span>
        </button>
        <button
          className={`tab-btn ${activeTab === "terminees" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("terminees")}
        >
          <i className="fa-solid fa-box-archive"></i>
          Sessions terminées
          <span className="tab-count">{sessionsTerminees.length}</span>
        </button>
      </div>

      {filterFormationById && (
        <div style={{
          background: "#E8F0FE",
          borderRadius: "10px",
          padding: "12px 16px",
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderLeft: `4px solid #336699`
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <i className="fa-solid fa-filter" style={{ color: "#336699", fontSize: "16px" }}></i>
            <span>
              <strong style={{ color: "#336699" }}>Filtre actif :</strong> Sessions de la formation 
              <strong style={{ color: "#1e293b", marginLeft: "5px" }}>"{filterFormationByName || `ID: ${filterFormationById}`}"</strong>
            </span>
          </div>
          <button
            onClick={resetFilterById}
            style={{
              background: "none",
              border: "none",
              color: "#336699",
              cursor: "pointer",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "13px"
            }}
          >
            <i className="fa-solid fa-times"></i>
            Réinitialiser
          </button>
        </div>
      )}

      {/* ═══════════════ ONGLET ACTIVES ═══════════════ */}
      {activeTab === "actives" && (
        <>
          <div className="toolbar">
            <div className="toolbar-left">
              <div className="search-box">
                <i className="fa-solid fa-magnifying-glass"></i>
                <input
                  type="text"
                  placeholder="Rechercher une session..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                />
              </div>
              <FilterDropdown
                icon="fa-book-open"
                placeholder="Toutes les formations"
                items={formationNoms}
                selectedValue={filterFormation}
                onSelect={(v) => { setFilterFormation(v); setCurrentPage(1); }}
                getLabel={(v) => v}
              />
              <FilterDropdown
                icon="fa-layer-group"
                placeholder="Tous les niveaux"
                items={niveaux}
                selectedValue={filterNiveau}
                onSelect={(v) => { setFilterNiveau(v); setCurrentPage(1); }}
                getLabel={(v) => NIVEAU_LABELS[v] || v}
              />
            </div>
            <div className="toolbar-right">
              {peutGerer && (
                <button
                  className="btn btn-add"
                  onClick={() => {
                    setFormAjout(EMPTY_FORM);
                    setErreursAjout({});
                    setErrServeurAjout("");
                    setModalAjout(true);
                  }}
                >
                  <i className="fa-solid fa-plus"></i> Nouvelle Session
                </button>
              )}
            </div>
          </div>

          {estGestion && selectedIds.length > 0 && (
            <div className="bulk-action-bar bulk-bar-archive">
              <div className="bulk-action-info">
                <div className="bulk-count-badge">
                  <i className="fa-solid fa-check"></i>
                  <span>{selectedIds.length}</span>
                </div>
                <span className="bulk-label">session(s) sélectionnée(s)</span>
              </div>
              <div className="bulk-action-btns">
                <button
                  className="bulk-btn bulk-btn-suppr"
                  disabled={bulkDeleting}
                  onClick={async () => {
                    if (!window.confirm(`Supprimer ${selectedIds.length} session(s) ?`)) return;
                    setBulkDeleting(true);
                    try {
                      const { supprimees, echecs } = await supprimerSessionsAvecConfirmation(selectedIds);
                      await fetchAll();
                      await fetchTerminees();
                      setSelectedIds([]);
                      if (supprimees) afficherSucces(`${supprimees} session(s) supprimée(s)`);
                      if (echecs.length) alert(`${echecs.length} session(s) non supprimée(s) : ${echecs[0]}`);
                    } finally {
                      setBulkDeleting(false);
                    }
                  }}
                >
                  {bulkDeleting ? <><i className="fa-solid fa-spinner fa-spin"></i> Suppression...</> : <><i className="fa-solid fa-trash"></i> Supprimer</>}
                </button>
                <button className="bulk-btn bulk-btn-cancel" onClick={() => setSelectedIds([])}>
                  <i className="fa-solid fa-xmark"></i> Annuler
                </button>
              </div>
            </div>
          )}

          <div className="table-card">
            <div className="table-top">
              Affichage de <strong>{paginated.length}</strong> sur <strong>{filteredActives.length}</strong> sessions
            </div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    {estGestion && (
                      <th style={{ width: "48px", textAlign: "center", paddingLeft: "16px" }}>
                        <label className="cb-wrap">
                          <input type="checkbox" className="cb-input" checked={allChecked}
                            ref={(el) => { if (el) el.indeterminate = someChecked; }}
                            onChange={toggleSelectAll} />
                          <span className="cb-box"></span>
                        </label>
                      </th>
                    )}
                    <th>#</th>
                    <th>Intitulé</th>
                    <th>Formation</th>
                    <th>Niveau</th>
                    <th>Dates</th>
                    <th>Formateurs</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 ? (
                    <tr className="empty-row">
                      <td colSpan={estGestion ? "9" : "8"} className="empty-cell">
                        <i className="fa-solid fa-inbox"></i>
                        <p>Aucune session trouvée</p>
                       </td>
                     </tr>
                  ) : (
                    paginated.map((s, idx) => {
                      const isReported = s.statut_session === 'reportee';
                      return (
                        <tr key={s.id} className={isReported ? "row-reported" : ""}>
                          {estGestion && (
                            <td style={{ textAlign: "center", paddingLeft: "16px" }}>
                              <label className="cb-wrap">
                                <input type="checkbox" className="cb-input"
                                  checked={selectedIds.includes(s.id)}
                                  onChange={() => toggleSelect(s.id)} />
                                <span className="cb-box"></span>
                              </label>
                            </td>
                          )}
                          <td className="td-num">{startIdx + idx + 1}</td>
                          <td className="td-title">{s.intitule_session}</td>
                          <td><span className="cat-tag">{s.formation_nom}</span></td>
                          <td><NiveauBadge niveau={s.niveau} /></td>
                          <td style={{ fontSize: "12px", color: "#475569", whiteSpace: "nowrap" }}>
                            {isReported ? (
                              <span style={{ color: "#f59e0b", fontWeight: "500" }}>
                                <i className="fa-solid fa-clock"></i> Date non définie
                              </span>
                            ) : (
                              `${fmtDate(s.date_debut)} → ${fmtDate(s.date_fin)}`
                            )}
                          </td>
                          <td>
                            {s.formateurs_detail && s.formateurs_detail.length > 0 ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                {s.formateurs_detail.slice(0, 2).map((f) => (
                                  <span key={f.id} style={{ fontSize: "12px", color: "#475569" }}>
                                    <i className="fa-solid fa-user" style={{ color: "#33CCFF", marginRight: "4px", fontSize: "10px" }}></i>
                                    {f.nom_complet || `${f.prenom || ''} ${f.nom || ''}`}
                                  </span>
                                ))}
                                {s.formateurs_detail.length > 2 && (
                                  <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                                    +{s.formateurs_detail.length - 2} autre(s)
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: "#94a3b8", fontSize: "12px" }}>—</span>
                            )}
                          </td>
                          <td><StatutBadge statut={s.statut_session} /></td>
                          <td className="td-actions">
                            <button className="act-btn act-detail" title="Détail" onClick={() => setModalDetail(s)}>
                              <i className="fa-solid fa-eye"></i>
                            </button>
                            {peutGerer && (
                              <>
                                <button className="act-btn act-modif" title="Modifier" onClick={() => openModif(s)}>
                                  <i className="fa-solid fa-pen"></i>
                                </button>
                                {s.statut_session !== 'reportee' && s.statut_session !== 'terminee' && (
                                  <button 
                                    className="act-btn act-report" 
                                    title="Reporter" 
                                    onClick={() => handleReporter(s)}
                                    disabled={reportLoading === s.id}
                                  >
                                    {reportLoading === s.id ? 
                                      <i className="fa-solid fa-spinner fa-spin"></i> : 
                                      <i className="fa-solid fa-calendar-week"></i>
                                    }
                                  </button>
                                )}
                                {s.statut_session === 'reportee' && (
                                  <button 
                                    className="act-btn act-reprogram" 
                                    title="Reprogrammer" 
                                    onClick={() => setModalReprogrammer(s)}
                                  >
                                    <i className="fa-solid fa-calendar-plus"></i>
                                  </button>
                                )}
                                {estGestion && <button className="act-btn act-suppr" title="Supprimer" onClick={() => setModalSuppr(s)}>
                                  <i className="fa-solid fa-trash"></i>
                                </button>}
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button className="pg-btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}>
                <i className="fa-solid fa-angle-left"></i>
              </button>
              <span className="pg-info">Page <strong>{safePage}</strong> sur <strong>{totalPages}</strong></span>
              <button className="pg-btn" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>
                <i className="fa-solid fa-angle-right"></i>
              </button>
            </div>
          )}
        </>
      )}

      {/* ═══════════════ ONGLET TERMINÉES ═══════════════ */}
      {activeTab === "terminees" && (
        <>
          <div className="archive-info-banner">
            <i className="fa-solid fa-circle-info"></i>
            <span>
              Les sessions dont la date de fin est dépassée sont automatiquement archivées ici.
            </span>
          </div>

          <div className="toolbar">
            <div className="toolbar-left">
              <div className="search-box">
                <i className="fa-solid fa-magnifying-glass"></i>
                <input
                  type="text"
                  placeholder="Rechercher dans les archives..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPageTerm(1); }}
                />
              </div>
              <FilterDropdown
                icon="fa-book-open"
                placeholder="Toutes les formations"
                items={formationNomsTerminees}
                selectedValue={filterFormationTerm}
                onSelect={(v) => { setFilterFormationTerm(v); setCurrentPageTerm(1); }}
                getLabel={(v) => v}
              />
              <FilterDropdown
                icon="fa-layer-group"
                placeholder="Tous les niveaux"
                items={niveaux}
                selectedValue={filterNiveauTerm}
                onSelect={(v) => { setFilterNiveauTerm(v); setCurrentPageTerm(1); }}
                getLabel={(v) => NIVEAU_LABELS[v] || v}
              />
            </div>
            <div className="toolbar-right">
              <button 
                className="btn btn-refresh" 
                onClick={() => {
                  fetchTerminees();
                  afficherSucces("Liste des sessions terminées actualisée");
                }}
                style={{ background: "#f1f5f9", color: "#475569" }}
              >
                <i className="fa-solid fa-rotate-right"></i> Rafraîchir
              </button>
            </div>
          </div>

          <div className="table-card table-card-archive">
            <div className="table-top">
              Affichage de <strong>{paginatedTerm.length}</strong> sur <strong>{filteredTerminees.length}</strong> sessions terminées
            </div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Intitulé</th>
                    <th>Formation</th>
                    <th>Niveau</th>
                    <th>Date début</th>
                    <th>Date fin</th>
                    <th>Formateurs</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTerm.length === 0 ? (
                    <tr className="empty-row">
                      <td colSpan="9" className="empty-cell">
                        <i className="fa-solid fa-box-archive"></i>
                        <p>Aucune session terminée</p>
                      </td>
                    </tr>
                  ) : (
                    paginatedTerm.map((s, idx) => (
                      <tr key={s.id}>
                        <td className="td-num">{startIdxTerm + idx + 1}</td>
                        <td className="td-title" style={{ opacity: 0.75 }}>{s.intitule_session}</td>
                        <td><span className="cat-tag">{s.formation_nom}</span></td>
                        <td><NiveauBadge niveau={s.niveau} /></td>
                        <td style={{ fontSize: "12px", color: "#475569" }}>{fmtDate(s.date_debut)}</td>
                        <td style={{ fontSize: "12px", color: "#475569" }}>{fmtDate(s.date_fin)}</td>
                        <td>
                          {s.formateurs_detail && s.formateurs_detail.length > 0 ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                              {s.formateurs_detail.slice(0, 2).map((f) => (
                                <span key={f.id} style={{ fontSize: "12px", color: "#475569" }}>
                                  <i className="fa-solid fa-user" style={{ color: "#33CCFF", marginRight: "4px", fontSize: "10px" }}></i>
                                  {f.nom_complet || `${f.prenom || ''} ${f.nom || ''}`}
                                </span>
                              ))}
                              {s.formateurs_detail.length > 2 && (
                                <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                                  +{s.formateurs_detail.length - 2} autre(s)
                                </span>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: "#94a3b8", fontSize: "12px" }}>—</span>
                          )}
                        </td>
                        <td><StatutBadge statut={s.statut_session} /></td>
                        <td className="td-actions">
                          <button className="act-btn act-detail" title="Détail" onClick={() => setModalDetail(s)}>
                            <i className="fa-solid fa-eye"></i>
                          </button>
                          {estGestion && (
                            <button className="act-btn act-suppr" title="Supprimer" onClick={() => setModalSuppr(s)}>
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalPagesTerm > 1 && (
            <div className="pagination">
              <button className="pg-btn" onClick={() => setCurrentPageTerm((p) => Math.max(1, p - 1))} disabled={safePageTerm === 1}>
                <i className="fa-solid fa-angle-left"></i>
              </button>
              <span className="pg-info">Page <strong>{safePageTerm}</strong> sur <strong>{totalPagesTerm}</strong></span>
              <button className="pg-btn" onClick={() => setCurrentPageTerm((p) => Math.min(totalPagesTerm, p + 1))} disabled={safePageTerm === totalPagesTerm}>
                <i className="fa-solid fa-angle-right"></i>
              </button>
            </div>
          )}
        </>
      )}

      {/* MODAL DE CONFIRMATION REPORT (ORANGE PASTEL) - accessible uniquement au Super Admin */}
      {peutGerer && modalReportConfirm && (
        <div className="modal-overlay show" onClick={(e) => { if (e.target === e.currentTarget && !reportLoading) setModalReportConfirm(null); }}>
          <div className="modal modal-report-confirm">
            <div className="modal-report-icon-wrap">
              <div className="modal-report-icon-circle">
                <i className="fa-solid fa-calendar-week"></i>
              </div>
            </div>
            
            <div className="modal-body" style={{ textAlign: "center", paddingTop: "0" }}>
              <h2 className="modal-report-title">Reporter la session</h2>
              
              <div className="modal-report-card">
                <div className="modal-report-avatar">
                  {modalReportConfirm.intitule_session?.slice(0, 2).toUpperCase() || "SF"}
                </div>
                <div className="modal-report-card-info">
                  <div className="modal-report-name">{modalReportConfirm.intitule_session}</div>
                  <div className="modal-report-sub">
                    #{String(modalReportConfirm.id).padStart(3, "0")}
                  </div>
                </div>
              </div>
              
              <div className="modal-report-warning">
                <i className="fa-solid fa-triangle-exclamation"></i>
                <div className="modal-report-warning-text">
                  <strong>Attention :</strong> Les dates seront effacées et la session passera en statut <strong>"Reportée"</strong>.
                </div>
              </div>
              
              <div className="modal-report-info">
                <i className="fa-solid fa-clock"></i>
                <span>Vous pourrez reprogrammer la session ultérieurement.</span>
              </div>
            </div>
            
            <div className="modal-footer modal-report-footer">
              <button 
                className="btn btn-cancel btn-report-annuler" 
                onClick={() => setModalReportConfirm(null)} 
                disabled={reportLoading}
              >
                <i className="fa-solid fa-xmark"></i> Annuler
              </button>
              <button 
                className="btn btn-report-confirmer" 
                onClick={async () => {
                  setReportLoading(modalReportConfirm.id);
                  try {
                    await reporterSession(modalReportConfirm.id);
                    await fetchAll();
                    await fetchTerminees();
                    afficherSucces(`Session "${modalReportConfirm.intitule_session}" a été reportée`);
                    setModalReportConfirm(null);
                  } catch (err) {
                    console.error("Erreur lors du report:", err);
                    alert("Erreur lors du report de la session");
                  } finally {
                    setReportLoading(null);
                  }
                }}
                disabled={reportLoading === modalReportConfirm.id}
              >
                {reportLoading === modalReportConfirm.id ? 
                  <><i className="fa-solid fa-spinner fa-spin"></i> Report en cours...</> : 
                  <><i className="fa-solid fa-calendar-week"></i> Confirmer le report</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL REPROGRAMMER - accessible uniquement au Super Admin */}
      {peutGerer && modalReprogrammer && (
        <ReprogrammerModal
          session={modalReprogrammer}
          onClose={() => setModalReprogrammer(null)}
          onReprogrammer={(dates) => handleReprogrammer(modalReprogrammer, dates)}
          loading={submitLoading}
        />
      )}

      {/* MODAL DÉTAIL - accessible à tous */}
      {modalDetail && (
        <div className="modal-overlay show" onClick={(e) => handleOverlay(e, () => setModalDetail(null))}>
          <div className="modal modal-wide modal-detail">
            <div className="modal-header detail-header">
              <div className="detail-header-left">
                <div className="detail-icon-wrap"><i className="fa-solid fa-calendar-days"></i></div>
                <div className="detail-header-info">
                  <h2>{modalDetail.intitule_session}</h2>
                  <div className="detail-badges">
                    <NiveauBadge niveau={modalDetail.niveau} />
                    <ModeBadge mode={modalDetail.mode} />
                    <StatutBadge statut={modalDetail.statut_session} />
                  </div>
                </div>
              </div>
              <button className="modal-close" onClick={() => setModalDetail(null)}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            
            <div className="modal-body">
              <div className="formation-info-card">
                <div className="formation-info-card-title">
                  <i className="fa-solid fa-book-open"></i> Formation associée
                </div>
                <div className="formation-info-name">{modalDetail.formation_nom}</div>
                <div className="formation-info-meta">
                  {modalDetail.formation_categorie && (
                    <span className="cat-tag">{modalDetail.formation_categorie}</span>
                  )}
                  {modalDetail.formation_duree && (
                    <span className="fmt-tag"><i className="fa-solid fa-clock"></i> {modalDetail.formation_duree} heures</span>
                  )}
                </div>
                
                {modalDetail.formation_description && modalDetail.formation_description !== "" && (
                  <div className="formation-info-section">
                    <div className="section-title">
                      <i className="fa-solid fa-align-left"></i> Description
                    </div>
                    <div className="section-content">{modalDetail.formation_description}</div>
                  </div>
                )}
                
                {modalDetail.formation_objectifs && modalDetail.formation_objectifs !== "" && (
                  <div className="formation-info-section">
                    <div className="section-title">
                      <i className="fa-solid fa-bullseye"></i> Objectifs pédagogiques
                    </div>
                    <div className="section-content">{modalDetail.formation_objectifs}</div>
                  </div>
                )}
                
                {modalDetail.formation_prerequis && modalDetail.formation_prerequis !== "" && (
                  <div className="formation-info-section">
                    <div className="section-title">
                      <i className="fa-solid fa-circle-check"></i> Prérequis
                    </div>
                    <div className="section-content">{modalDetail.formation_prerequis}</div>
                  </div>
                )}
              </div>

              <div className="detail-stats">
                <div className="stat-card sc-green">
                  <div className="sc-icon"><i className="fa-solid fa-money-bill-wave"></i></div>
                  <div className="sc-info">
                    <span className="sc-val">{parseFloat(modalDetail.prix_ht).toFixed(2)} DT</span>
                    <span className="sc-lbl">Prix HT</span>
                  </div>
                </div>
                <div className="stat-card sc-navy">
                  <div className="sc-icon"><i className="fa-solid fa-receipt"></i></div>
                  <div className="sc-info">
                    <span className="sc-val">{parseFloat(modalDetail.prix_ttc).toFixed(2)} DT</span>
                    <span className="sc-lbl">Prix TTC</span>
                  </div>
                </div>
                <div className="stat-card sc-blue">
                  <div className="sc-icon"><i className="fa-solid fa-layer-group"></i></div>
                  <div className="sc-info">
                    <span className="sc-val">{modalDetail.tranche}</span>
                    <span className="sc-lbl">Tranches</span>
                  </div>
                </div>
                <div className="stat-card sc-sand">
                  <div className="sc-icon"><i className="fa-solid fa-users"></i></div>
                  <div className="sc-info">
                    <span className="sc-val">{modalDetail.formateurs_detail?.length || 0}</span>
                    <span className="sc-lbl">Formateurs</span>
                  </div>
                </div>
              </div>

              <div className="detail-dates">
                <div className="date-block">
                  <i className="fa-solid fa-calendar-plus"></i>
                  <div>
                    <span className="date-lbl">Date de début</span>
                    <span className="date-val">{modalDetail.date_debut ? fmtDate(modalDetail.date_debut) : <span style={{ color: "#f59e0b" }}>Non définie</span>}</span>
                  </div>
                </div>
                <i className="fa-solid fa-arrow-right date-arrow"></i>
                <div className="date-block">
                  <i className="fa-solid fa-calendar-check"></i>
                  <div>
                    <span className="date-lbl">Date de fin</span>
                    <span className="date-val">{modalDetail.date_fin ? fmtDate(modalDetail.date_fin) : <span style={{ color: "#f59e0b" }}>Non définie</span>}</span>
                  </div>
                </div>
              </div>

              <div className="detail-sec">
                <div className="detail-sec-title">
                  <i className="fa-solid fa-chalkboard-user"></i> Formateurs assignés ({modalDetail.formateurs_detail?.length || 0})
                </div>
                <div className="fmt-detail-list">
                  {modalDetail.formateurs_detail && modalDetail.formateurs_detail.length > 0 ? (
                    modalDetail.formateurs_detail.map((f) => (
                      <div key={f.id} className="fmt-detail-item">
                        <div className="fmt-avatar">
                          {((f.prenom && f.prenom[0]) || (f.nom && f.nom[0]) || "?")}
                        </div>
                        <div className="fmt-detail-info">
                          <span className="fmt-name-detail">
                            {f.nom_complet || `${f.prenom || ''} ${f.nom || ''}`.trim() || "Sans nom"}
                          </span>
                          {f.specialites && f.specialites !== "" && (
                            <span className="fmt-specialite">{f.specialites}</span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="fmt-empty-message">
                      <i className="fa-solid fa-user-slash"></i>
                      <p>Aucun formateur assigné à cette session</p>
                    </div>
                  )}
                </div>
              </div>

              {/* 👇 SECTION ÉTUDIANTS AJOUTÉE */}
              <SessionEtudiantsSection 
                sessionId={modalDetail.id} 
                onViewDetail={(etudiantId) => setModalDetailEtudiant(etudiantId)}
              />
            </div>
            
            <div className="modal-footer">
              <button className="btn btn-cancel" onClick={() => setModalDetail(null)}>Fermer</button>
              {peutGerer && modalDetail.statut_session !== 'terminee' && (
                <button className="btn btn-update" onClick={() => {
                  const session = modalDetail;
                  setModalDetail(null);
                  openModif(session);
                }}>
                  <i className="fa-solid fa-pen"></i> Modifier
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 👇 MODAL DÉTAIL ÉTUDIANT AJOUTÉ */}
      <EtudiantDetailModal
        etudiantId={modalDetailEtudiant}
        visible={!!modalDetailEtudiant}
        onClose={() => setModalDetailEtudiant(null)}
      />

      {/* MODAL AJOUT - accessible uniquement au Super Admin */}
      {peutGerer && modalAjout && (
        <div className="modal-overlay show" onClick={(e) => handleOverlay(e, () => { if (!submitLoading) setModalAjout(false); })}>
          <div className="modal" ref={modalAjoutRef}>
            <div className="modal-header">
              <h2><i className="fa-solid fa-plus"></i> Nouvelle Session de Formation</h2>
              <button className="modal-close" onClick={() => setModalAjout(false)}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="modal-body">
              {errServeurAjout && (
                <div className="error-server">
                  <i className="fa-solid fa-circle-exclamation"></i> {errServeurAjout}
                </div>
              )}
              {erreursAjout.dates && (
                <div className="error-duration">
                  <i className="fa-solid fa-clock"></i>
                  <span>{erreursAjout.dates}</span>
                </div>
              )}
              <div className="form-grid">
                <div className="form-group full">
                  <label>Intitulé de la session <span className="req">*</span></label>
                  <input
                    type="text"
                    className={erreursAjout.intitule_session ? "input-error" : ""}
                    value={formAjout.intitule_session}
                    onChange={(e) => { setFormAjout({ ...formAjout, intitule_session: e.target.value }); setErreursAjout({ ...erreursAjout, intitule_session: "" }); }}
                    placeholder="Nom de la session"
                  />
                  <small className="error-msg">{erreursAjout.intitule_session}</small>
                </div>
                <div className="form-group full">
                  <label>Formation <span className="req">*</span></label>
                  <FormationFormSelect
                    formations={formations}
                    value={formAjout.formation}
                    onChange={(val) => { setFormAjout({ ...formAjout, formation: val }); setErreursAjout({ ...erreursAjout, formation: "" }); }}
                    hasError={!!erreursAjout.formation}
                  />
                  <small className="error-msg">{erreursAjout.formation}</small>
                </div>
                <div className="form-group full">
                  <label>Formateurs</label>
                  <FormateursMultiSelect
                    formateurs={formateurs}
                    value={formAjout.formateurs}
                    onChange={(val) => setFormAjout({ ...formAjout, formateurs: val })}
                  />
                </div>
                <div className="form-group">
                  <label>Date de début <span className="req">*</span></label>
                  <input
                    type="date"
                    className={erreursAjout.date_debut ? "input-error" : ""}
                    value={formAjout.date_debut}
                    onChange={(e) => { 
                      setFormAjout({ ...formAjout, date_debut: e.target.value });
                      setErreursAjout({ ...erreursAjout, date_debut: "", date_fin: "" });
                    }}
                  />
                  <small className="error-msg">{erreursAjout.date_debut}</small>
                </div>
                <div className="form-group">
                  <label>Date de fin <span className="req">*</span></label>
                  <input
                    type="date"
                    min={formAjout.date_debut || undefined}
                    className={erreursAjout.date_fin ? "input-error" : ""}
                    value={formAjout.date_fin}
                    onChange={(e) => { 
                      setFormAjout({ ...formAjout, date_fin: e.target.value });
                      setErreursAjout({ ...erreursAjout, date_fin: "" });
                    }}
                  />
                  <small className="error-msg">{erreursAjout.date_fin}</small>
                </div>
                <div className="form-group full">
                  <SeancesEditor
                    key={"ajout"}
                    dateDebut={formAjout.date_debut}
                    dateFin={formAjout.date_fin}
                    seances={formAjout.seances}
                    onChange={changerSeancesAjout}
                    erreur={erreursAjout.seances}
                  />
                  <ConflitSalle
                    messages={conflitAjout}
                    enCours={submitLoading}
                    libelleChanger="Modifier les séances"
                    onChanger={() => setConflitAjout(null)}
                    onMaintenir={() => handleAjout({ salle_partagee: true })}
                  />
                </div>
                <div className="form-group">
                  <label>Mode</label>
                  <select value={formAjout.mode} onChange={(e) => setFormAjout({ ...formAjout, mode: e.target.value })}>
                    <option value="presentiel">Présentiel</option>
                    <option value="distanciel">Distanciel</option>
                    <option value="hybride">Hybride</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Niveau (optionnel)</label>
                  <select value={formAjout.niveau || ""} onChange={(e) => setFormAjout({ ...formAjout, niveau: e.target.value })}>
                    <option value="">-- Aucun --</option>
                    <option value="debutant">Débutant</option>
                    <option value="intermediaire">Intermédiaire</option>
                    <option value="avance">Avancé</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Prix HT (DT)</label>
                  <input
                    type="number" step="0.01" min="0"
                    className={erreursAjout.prix_ht ? "input-error" : ""}
                    value={formAjout.prix_ht}
                    onChange={(e) => { setFormAjout({ ...formAjout, prix_ht: e.target.value }); setErreursAjout({ ...erreursAjout, prix_ht: "", prix_ttc: "" }); }}
                  />
                  <small className="error-msg">{erreursAjout.prix_ht}</small>
                </div>
                <div className="form-group">
                  <label>Prix TTC (DT)</label>
                  <input
                    type="number" step="0.01" min="0"
                    className={erreursAjout.prix_ttc ? "input-error" : ""}
                    value={formAjout.prix_ttc}
                    onChange={(e) => { setFormAjout({ ...formAjout, prix_ttc: e.target.value }); setErreursAjout({ ...erreursAjout, prix_ttc: "" }); }}
                  />
                  <small className="error-msg">{erreursAjout.prix_ttc}</small>
                </div>
                <div className="form-group">
                  <label>Nombre de tranches</label>
                  <input
                    type="number" min="1"
                    className={erreursAjout.tranche ? "input-error" : ""}
                    value={formAjout.tranche}
                    onChange={(e) => { setFormAjout({ ...formAjout, tranche: e.target.value }); setErreursAjout({ ...erreursAjout, tranche: "" }); }}
                  />
                  <small className="error-msg">{erreursAjout.tranche}</small>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-cancel" onClick={() => setModalAjout(false)} disabled={submitLoading}>Annuler</button>
              <button className="btn btn-add" onClick={handleAjout} disabled={submitLoading}>
                {submitLoading ? <><i className="fa-solid fa-spinner fa-spin"></i> En cours…</> : <><i className="fa-solid fa-plus"></i> Ajouter</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL MODIFICATION - accessible uniquement au Super Admin */}
      {peutGerer && modalModif && (
        <div className="modal-overlay show" onClick={(e) => handleOverlay(e, () => { if (!submitLoading) setModalModif(null); })}>
          <div className="modal" ref={modalModifRef}>
            <div className="modal-header modif-header">
              <h2><i className="fa-solid fa-pen"></i> Modifier la Session</h2>
              <button className="modal-close" onClick={() => setModalModif(null)}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="modal-body">
              {errServeurModif && (
                <div className="error-server">
                  <i className="fa-solid fa-circle-exclamation"></i> {errServeurModif}
                </div>
              )}
              {erreursModif.dates && (
                <div className="error-duration">
                  <i className="fa-solid fa-clock"></i>
                  <span>{erreursModif.dates}</span>
                </div>
              )}
              <div className="form-grid">
                <div className="form-group full">
                  <label>Intitulé de la session <span className="req">*</span></label>
                  <input
                    type="text"
                    className={erreursModif.intitule_session ? "input-error" : ""}
                    value={formModif.intitule_session}
                    onChange={(e) => { setFormModif({ ...formModif, intitule_session: e.target.value }); setErreursModif({ ...erreursModif, intitule_session: "" }); }}
                  />
                  <small className="error-msg">{erreursModif.intitule_session}</small>
                </div>
                <div className="form-group full">
                  <label>Formation <span className="req">*</span></label>
                  <FormationFormSelect
                    formations={formations}
                    value={formModif.formation}
                    onChange={(val) => { setFormModif({ ...formModif, formation: val }); setErreursModif({ ...erreursModif, formation: "" }); }}
                    hasError={!!erreursModif.formation}
                  />
                  <small className="error-msg">{erreursModif.formation}</small>
                </div>
                <div className="form-group full">
                  <label>Formateurs</label>
                  <FormateursMultiSelect
                    formateurs={formateurs}
                    value={formModif.formateurs}
                    onChange={(val) => setFormModif({ ...formModif, formateurs: val })}
                  />
                </div>
                <div className="form-group">
                  <label>Date de début <span className="req">*</span></label>
                  <input
                    type="date"
                    className={erreursModif.date_debut ? "input-error" : ""}
                    value={formModif.date_debut}
                    onChange={(e) => { 
                      setFormModif({ ...formModif, date_debut: e.target.value });
                      setErreursModif({ ...erreursModif, date_debut: "", date_fin: "" });
                    }}
                  />
                  <small className="error-msg">{erreursModif.date_debut}</small>
                </div>
                <div className="form-group">
                  <label>Date de fin <span className="req">*</span></label>
                  <input
                    type="date"
                    min={formModif.date_debut || undefined}
                    className={erreursModif.date_fin ? "input-error" : ""}
                    value={formModif.date_fin}
                    onChange={(e) => { 
                      setFormModif({ ...formModif, date_fin: e.target.value });
                      setErreursModif({ ...erreursModif, date_fin: "" });
                    }}
                  />
                  <small className="error-msg">{erreursModif.date_fin}</small>
                </div>
                <div className="form-group full">
                  <SeancesEditor
                    key={modalModif.id}
                    dateDebut={formModif.date_debut}
                    dateFin={formModif.date_fin}
                    seances={formModif.seances}
                    onChange={changerSeancesModif}
                    erreur={erreursModif.seances}
                  />
                  <ConflitSalle
                    messages={conflitModif}
                    enCours={submitLoading}
                    libelleChanger="Modifier les séances"
                    onChanger={() => setConflitModif(null)}
                    onMaintenir={() => handleModif({ salle_partagee: true })}
                  />
                </div>
                <div className="form-group">
                  <label>Mode</label>
                  <select value={formModif.mode} onChange={(e) => setFormModif({ ...formModif, mode: e.target.value })}>
                    <option value="presentiel">Présentiel</option>
                    <option value="distanciel">Distanciel</option>
                    <option value="hybride">Hybride</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Niveau (optionnel)</label>
                  <select value={formModif.niveau || ""} onChange={(e) => setFormModif({ ...formModif, niveau: e.target.value })}>
                    <option value="">-- Aucun --</option>
                    <option value="debutant">Débutant</option>
                    <option value="intermediaire">Intermédiaire</option>
                    <option value="avance">Avancé</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Prix HT (DT)</label>
                  <input
                    type="number" step="0.01" min="0"
                    className={erreursModif.prix_ht ? "input-error" : ""}
                    value={formModif.prix_ht}
                    onChange={(e) => { setFormModif({ ...formModif, prix_ht: e.target.value }); setErreursModif({ ...erreursModif, prix_ht: "", prix_ttc: "" }); }}
                  />
                  <small className="error-msg">{erreursModif.prix_ht}</small>
                </div>
                <div className="form-group">
                  <label>Prix TTC (DT)</label>
                  <input
                    type="number" step="0.01" min="0"
                    className={erreursModif.prix_ttc ? "input-error" : ""}
                    value={formModif.prix_ttc}
                    onChange={(e) => { setFormModif({ ...formModif, prix_ttc: e.target.value }); setErreursModif({ ...erreursModif, prix_ttc: "" }); }}
                  />
                  <small className="error-msg">{erreursModif.prix_ttc}</small>
                </div>
                <div className="form-group">
                  <label>Nombre de tranches</label>
                  <input
                    type="number" min="1"
                    className={erreursModif.tranche ? "input-error" : ""}
                    value={formModif.tranche}
                    onChange={(e) => { setFormModif({ ...formModif, tranche: e.target.value }); setErreursModif({ ...erreursModif, tranche: "" }); }}
                  />
                  <small className="error-msg">{erreursModif.tranche}</small>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-cancel" onClick={() => setModalModif(null)} disabled={submitLoading}>Annuler</button>
              <button className="btn btn-update" onClick={handleModif} disabled={submitLoading}>
                {submitLoading ? <><i className="fa-solid fa-spinner fa-spin"></i> En cours…</> : <><i className="fa-solid fa-rotate"></i> Mettre à jour</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SUPPRESSION - accessible uniquement au Super Admin */}
      {estGestion && modalSuppr && (
        <div className="modal-overlay show" onClick={(e) => handleOverlay(e, () => { if (!submitLoading) setModalSuppr(null); })}>
          <div className="modal modal-suppr">
            <div className="modal-suppr-icon-wrap">
              <div className="modal-suppr-icon-circle">
                <i className="fa-solid fa-trash-can"></i>
              </div>
            </div>
            <div className="modal-body" style={{ textAlign: "center", paddingTop: "0" }}>
              <h2 className="modal-suppr-title-main">Supprimer la session</h2>
              <div className="modal-suppr-card">
                <div className="modal-suppr-avatar">
                  {modalSuppr.intitule_session?.slice(0, 2).toUpperCase() || "SF"}
                </div>
                <div className="modal-suppr-card-info">
                  <div className="modal-suppr-name">{modalSuppr.intitule_session}</div>
                  <div className="modal-suppr-sub">#{String(modalSuppr.id).padStart(3, "0")}</div>
                </div>
              </div>
              <div className="modal-suppr-warning">
                <i className="fa-solid fa-triangle-exclamation"></i>
                <span>Cette action est <strong>irréversible</strong>. Toutes les données associées seront définitivement supprimées.</span>
              </div>
            </div>
            <div className="modal-footer modal-suppr-footer">
              <button className="btn btn-cancel btn-suppr-annuler" onClick={() => setModalSuppr(null)} disabled={submitLoading}>
                <i className="fa-solid fa-xmark"></i> Annuler
              </button>
              <button className="btn btn-danger btn-suppr-confirmer" onClick={handleSupprimer} disabled={submitLoading}>
                {submitLoading
                  ? <><i className="fa-solid fa-spinner fa-spin"></i> Suppression...</>
                  : <><i className="fa-solid fa-trash-can"></i> Confirmer</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default SessionFormation;